import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

// Extend dayjs with timezone plugins
dayjs.extend(utc);
dayjs.extend(timezone);

// Default timezone: Japan (Asia/Tokyo, UTC+9)
export const DEFAULT_TIMEZONE = "Asia/Tokyo";

// Helper function to check if date string is in UTC format
const isUTCString = (date: any): boolean => {
  if (typeof date === "string") {
    // Check for UTC indicators: Z, +00:00, -00:00, or ISO format without timezone
    return (
      date.endsWith("Z") ||
      date.includes("+00:00") ||
      date.includes("-00:00") ||
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z?$/.test(date)
    );
  }
  return false;
};

// Helper function to parse date and convert to Japan timezone
const parseToJapanTimezone = (date?: dayjs.ConfigType) => {
  if (date === undefined) {
    // No date provided - get current time in Japan timezone
    return dayjs.tz(DEFAULT_TIMEZONE);
  }

  // If date is UTC format (from BE), parse as UTC then convert to Japan timezone
  // Example: "2025-12-10T06:45:18.000000Z" → parse as UTC → convert to Asia/Tokyo
  if (isUTCString(date)) {
    // Parse as UTC first, then convert to Japan timezone
    // This ensures UTC time from BE is correctly converted to JST (+9)
    return dayjs.utc(date).tz(DEFAULT_TIMEZONE);
  }

  // If date is already a dayjs object, convert to Japan timezone
  if (dayjs.isDayjs(date)) {
    return date.tz(DEFAULT_TIMEZONE);
  }

  // Otherwise, parse in Japan timezone (assume input is already in local/JST format)
  return dayjs.tz(date, DEFAULT_TIMEZONE);
};

// Helper function to get dayjs instance with Japan timezone
export const dayjsJP = (date?: dayjs.ConfigType) => {
  return parseToJapanTimezone(date);
};

// Override dayjs() to use Japan timezone by default
// This makes all dayjs() calls automatically use Asia/Tokyo timezone
// If date is UTC format (from BE), it will be converted to Japan timezone
const dayjsWithTimezone = ((date?: dayjs.ConfigType) => {
  return parseToJapanTimezone(date);
}) as typeof dayjs;

// Copy all static methods and properties from original dayjs
Object.keys(dayjs).forEach((key) => {
  if (key !== "prototype") {
    (dayjsWithTimezone as any)[key] = (dayjs as any)[key];
  }
});

// Preserve prototype chain
Object.setPrototypeOf(dayjsWithTimezone, dayjs);

// Export configured dayjs as default
export default dayjsWithTimezone;
